import { useCallback, useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import { useAgreementContext } from "../context";
import { useAgreementBreadcrumbs } from "../hooks";
import { AgreementMenu } from "./AgreementMenu";
import {
  AgreementApprovalSection,
  AgreementStatementsSection,
  AgreementQuestionnaireSection,
  AgreementB2BQuestionnaireSection,
  AgreementCommentsSection,
  AgreementDetailsSection,
  AgreementDocumentsSection,
  AgreementHistorySection,
} from "./sections";
import {
  AgreementAlert,
  AgreementLoader,
  AgreementQuickCommentPopover,
  AgreementTaxStatusChangedDialog,
} from "./shared";
import { Button } from "@/components/ui/button";
import { MessageSquarePlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { applyQuickComment } from "../utils";

const AgreementContent = () => {
  const { t } = useTranslation();
  const { data, errorState, loadingState, transitions, setComments } =
    useAgreementContext();
  const { breadcrumbItems } = useAgreementBreadcrumbs();
  const [isTaxStatusDialogOpen, setIsTaxStatusDialogOpen] = useState(false);

  const taxStatusChangedInfo = data?.tax_status_changed_info;

  useEffect(() => {
    setIsTaxStatusDialogOpen(Boolean(taxStatusChangedInfo));
  }, [taxStatusChangedInfo]);

  const handleQuickComment = useCallback(
    (commentText: string) => {
      setComments((prev) => applyQuickComment(prev, commentText));
    },
    [setComments],
  );

  const handleTaxStatusDialogClose = useCallback(() => {
    setIsTaxStatusDialogOpen(false);
  }, []);

  if (loadingState.isLoading) {
    return <AgreementLoader reason={loadingState.reason} />;
  }

  if (errorState.error) {
    return (
      <AgreementAlert
        title="errors.genericTitle"
        message={errorState.error.message}
      />
    );
  }

  if (!data) {
    return <AgreementAlert title="table.noData" message="agreement.noData" />;
  }

  return (
    <>
      <PageHeader breadcrumbItems={breadcrumbItems} />
      <AgreementMenu />
      <div className="space-y-6">
        <AgreementDetailsSection />
        <AgreementQuestionnaireSection />
        <AgreementB2BQuestionnaireSection />
        <AgreementStatementsSection />
        <AgreementDocumentsSection />
        <AgreementHistorySection />
        <AgreementCommentsSection />
        <AgreementApprovalSection />
      </div>

      {transitions.canComment && (
        <AgreementQuickCommentPopover onAddComment={handleQuickComment}>
          <Button
            className="fixed right-8 bottom-8 h-14 w-14 rounded-full shadow-lg hover:shadow-xl transition-shadow"
            size="icon"
            title={t("agreements.comments.add")}
          >
            <MessageSquarePlus className="h-6 w-6" />
          </Button>
        </AgreementQuickCommentPopover>
      )}

      {taxStatusChangedInfo && (
        <AgreementTaxStatusChangedDialog
          info={taxStatusChangedInfo}
          open={isTaxStatusDialogOpen}
          onClose={handleTaxStatusDialogClose}
        />
      )}
    </>
  );
};

export default AgreementContent;
